/* Public surface of `@shared/brand`. Always import from here, never
   from the individual files — that keeps refactors painless. */

export { Glyph } from "./Glyph";
export type { GlyphProps } from "./Glyph";

export { Wordmark } from "./Wordmark";
export type { WordmarkProps, ProductSuffix } from "./Wordmark";

export { Lockup } from "./Lockup";
export type { LockupProps } from "./Lockup";

export { FullLockup, DEFAULT_TAGLINE } from "./FullLockup";
export type { FullLockupProps } from "./FullLockup";

/* ──────────── Brand constants ──────────── */

export const BRAND_NAME = "CADtomie";
export const BRAND_DOMAIN = "cadtomie.com";

/** Three-part tagline shown on the marketing surfaces and (optionally)
 *  on export footers. Keep in sync with the master brand asset. */
export const TAGLINE_PARTS = ["Plan", "Measure", "Analyze"] as const;
export const TAGLINE = "Plan · Measure · Analyze";

/** Standard subtitle that sits below the wordmark in the full lockup. */
export const BRAND_SUBTITLE = "Deformity analysis";

/** Disclaimer used in exports and the marketing footer. Identical
 *  wording across surfaces. */
export const DISCLAIMER =
  "For research and intraoperative assistance only · Not a medical device · Always verify with imaging";

/* ──────────── Product metadata ────────────
 * Single source of truth for product names, short-names, descriptions
 * and canonical URLs. Anything that needs to identify a product
 * (marketing cards, footer links, breadcrumbs, exports) reads from
 * here so renaming is a one-line change. */

export type ProductKey = "plan" | "intraop";

export interface ProductMeta {
  /** Internal key. */
  key: ProductKey;
  /** Full product name, e.g. "CADtomie.plan". */
  name: string;
  /** Short suffix-only name, e.g. ".plan". Useful in tight nav slots. */
  shortName: ".plan" | ".intraop";
  /** Surface label — "Pre-operative" / "Intraoperative". */
  surface: string;
  /** Device label — "Desktop" / "Smartphone". */
  device: string;
  /** One-line description for marketing cards. */
  description: string;
  /** Production URL — overrideable at build time via env. */
  defaultUrl: string;
}

export const PRODUCT: Record<ProductKey, ProductMeta> = {
  plan: {
    key: "plan",
    name: "CADtomie.plan",
    shortName: ".plan",
    surface: "Pre-operative",
    device: "Desktop · large display",
    description:
      "Full-leg axis analysis and osteotomy planning from X-ray images. HTO, DFO and tibial slope simulations update live as you adjust landmarks.",
    defaultUrl: "https://plan.cadtomie.com",
  },
  intraop: {
    key: "intraop",
    name: "CADtomie.intraop",
    shortName: ".intraop",
    surface: "Intraoperative",
    device: "Smartphone PWA",
    description:
      "Camera-and-sensor measurements that fit in your pocket. Osteotomy angle, height with optional mm calibration, and a live digital goniometer for torsional control.",
    defaultUrl: "https://intraop.cadtomie.com",
  },
};

/* ──────────── Module accents ──────────── */

export const MODULE_COLOR = {
  angle:   "#7cc4ff",
  height:  "#30d158",
  torsion: "#ff9f0a",
} as const;
export type ModuleKey = keyof typeof MODULE_COLOR;
